import { useEffect, useRef, useState } from 'react';
import { accounts, CredentialResponse } from 'google-one-tap';
import { parseCookies } from 'nookies';

import {
    consentNotShownInLastTenMins,
    CONSENT_COOKIE
} from '@zoopla/consent-utils';
import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { AuthType } from '../../../graphql.generated';
import {
    logNetworkAuthFailure,
    logSocialAuthFailure,
    logUserAuthFailure,
    trackAuthFailure,
    trackAuthSuccess
} from '../../AuthModal/hooks/authMutationHook.tracking';
import { AuthMutationHook } from '../../AuthModal/hooks/authMutationHook.types';
import { useAuth } from '../../AuthModal/hooks/useAuth';
import { getNextBestAction } from '../../AuthModal/utils/getNextBestAction';
import {
    SocialSignInGoogleOneTapDocument,
    SocialSignInGoogleOneTapMutation,
    SocialSignInGoogleOneTapMutationVariables
} from '../queries/googleOneTap.generated';
import { setupLogger } from '@zoopla/logger';

const logger = setupLogger({
    custodian: 'pnp',
    component: 'auth-modal'
});

export const useGoogleOneTap: AuthMutationHook<undefined> = ({
    trackingData
}) => {
    const hasNetworkError = useRef(false);
    // we're using local state here since we count on re-renders to refresh the callback
    const [isGoogleLoaded, setisGoogleLoaded] = useState(false);
    const { setCurrentSession } = useAuth();
    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SocialSignInGoogleOneTapMutation,
        SocialSignInGoogleOneTapMutationVariables
    >(SocialSignInGoogleOneTapDocument, {
        onError(error) {
            if (error && 'isNetworkError' in error) {
                hasNetworkError.current = true;
            }
        }
    });

    const oneTapCallbackRef = useRef<typeof signInMutation>();

    useEffect(() => {
        oneTapCallbackRef.current = signInMutation;
    });

    const init = () => {
        setisGoogleLoaded(true);
    };

    const trackError = () => {
        trackAuthFailure({
            source: 'google',
            type: 'signin',
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });
    };

    // We don't really count on the error messages here since we just swallow them
    const trackAndThrowError = (message: string) => {
        trackError();

        throw new Error(message);
    };

    const authenticationMutation = async (
        credentialResponse: CredentialResponse
    ) => {
        const response = await oneTapCallbackRef.current?.({
            accessToken: credentialResponse.credential
        });

        if (hasNetworkError.current) {
            logNetworkAuthFailure({ type: 'googleonetap' });
            return trackAndThrowError('NO_NETWORK');
        }

        if (!response || !response.data || response.errors) {
            return trackAndThrowError('NO_RESPONSE');
        }

        const { data } = response;

        if (data.socialSignInGoogleOneTapV2?.__typename === 'AuthError') {
            return trackAndThrowError('SERVER_ERROR');
        }

        if (!data.socialSignInGoogleOneTapV2?.user) {
            return trackAndThrowError('NO_USER');
        }

        // This covers the case we have seen in a few incidents with sessions
        // when the authentication API works well but the user one fails
        if (
            !data.socialSignInGoogleOneTapV2.user.loggedIn ||
            !data.socialSignInGoogleOneTapV2.user.userUuid
        ) {
            logUserAuthFailure({
                type: 'googleonetap',
                subtype: !data.socialSignInGoogleOneTapV2.user.userUuid
                    ? 'no_uuid'
                    : 'not_logged_in'
            });
            return trackAndThrowError('NOT_LOGGED_IN');
        }

        const {
            socialSignInGoogleOneTapV2: { authType, user }
        } = data;

        const isNewUserAuthentication = authType === AuthType.Register;

        const type = isNewUserAuthentication ? 'registration' : 'signin';

        // TODO: Observability replace with RUM solution
        // logAuthSuccess('google-auth');
        trackAuthSuccess({
            source: 'google',
            type,
            userObject: user,
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });

        setCurrentSession(user, isNewUserAuthentication);

        const nextBestActions = getNextBestAction(user);

        return { ...user, actions: nextBestActions };
    };

    const authenticate = async () => {
        if (!isGoogleLoaded) {
            init();
        }

        const consentCookie = parseCookies()[CONSENT_COOKIE];
        const isShowingZDSModal =
            document.querySelector('div[role="dialog"]') !== null;

        const googleAccounts: accounts = window.google.accounts;

        return new Promise<ReturnType<typeof authenticationMutation>>(
            (res, rej) => {
                if (
                    !consentNotShownInLastTenMins(consentCookie) ||
                    isShowingZDSModal
                ) {
                    // if we shouldn't show one tap just reject the promise
                    return rej(new Error('not_shown'));
                }

                // Note: TS get namespace `google` from the Google Maps package caused type error
                googleAccounts?.id.initialize({
                    client_id:
                        '696203622959-7kq1qaikjuk02838oc5ckv0t3a4329tq.apps.googleusercontent.com',
                    context: 'signin',
                    callback: (response) => {
                        if (response.credential) {
                            return res(authenticationMutation(response));
                        } else {
                            logSocialAuthFailure({
                                name: 'google-onetap-auth',
                                type: 'missing-credential'
                            });

                            trackError();

                            return rej(
                                new Error(
                                    'Something went wrong and you have not been signed in. Please try again.'
                                )
                            );
                        }
                    }
                });

                googleAccounts?.id.prompt((notification) => {
                    const isNotDisplayed = notification.isNotDisplayed();

                    if (isNotDisplayed || notification.isSkippedMoment()) {
                        logger.info(notification.getNotDisplayedReason());
                    }

                    if (isNotDisplayed) {
                        const reason = notification.getNotDisplayedReason();

                        if (
                            !reason ||
                            reason === 'opt_out_or_no_session' ||
                            reason === 'suppressed_by_user' ||
                            reason === 'browser_not_supported'
                        ) {
                            return;
                        }

                        logSocialAuthFailure({
                            name: 'google-onetap-auth',
                            type: reason
                        });

                        trackError();

                        return rej(
                            new Error(
                                'Something went wrong and you have not been signed in. Please try again.'
                            )
                        );
                    }
                });
            }
        );
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return authenticate;
};
